import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

import type { Invoice } from '@stuut/api/models';
import { InvoiceStatus } from '@stuut/api/models';

export const useInvoiceDisplay = (invoiceInput: MaybeRefOrGetter<Invoice | null | undefined>) => {
  const invoice = computed(() => toValue(invoiceInput));

  const isPaid = computed(
    () =>
      invoice.value?.status === InvoiceStatus.PAID || invoice.value?.pending_amount_remaining === 0,
  );

  const isVoid = computed(() => invoice.value?.status === InvoiceStatus.VOID);

  const isUncollectible = computed(() => invoice.value?.status === InvoiceStatus.UNCOLLECTIBLE);

  const showStamp = computed(() => isPaid.value);

  const stampColorClass = computed(() => {
    if (isPaid.value) return 'bg-success/10 text-success/40';
    return '';
  });

  const amountColorClass = computed(() => {
    if (isPaid.value) return 'text-success';
    if (isVoid.value || isUncollectible.value) return 'text-muted-foreground/80 line-through';
    return '';
  });

  const hasPartialProgress = computed(
    () =>
      invoice.value?.pending_amount_remaining &&
      invoice.value?.amount_due &&
      invoice.value?.pending_amount_remaining !== invoice.value?.amount_due,
  );

  const displayStatus = computed(() => {
    if (invoice.value?.isBrokenPromise) return InvoiceStatus.BROKEN_PAYMENT_PROMISE;

    if (invoice.value?.isOpen && invoice.value?.promised_to_pay_date)
      return InvoiceStatus.PAYMENT_PROMISED;

    if (invoice.value?.isOverdue) return InvoiceStatus.OVERDUE;

    return invoice.value?.status;
  });

  return {
    isPaid,
    isVoid,
    isUncollectible,
    showStamp,
    stampColorClass,
    amountColorClass,
    hasPartialProgress,
    displayStatus,
  };
};
